import React from "react";
import { Copy } from "lucide-react";
import { AlertTriangle } from "lucide-react";
import { Mail } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface UserData {
  id: string;
  user_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  language?: string;
  language_secondary?: string;
  country?: string;
  created_at?: string | null;
  journal_count?: number;
  is_placeholder?: boolean;
}

interface UserDetailDialogProps {
  user: UserData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const UserDetailDialog: React.FC<UserDetailDialogProps> = ({ 
  user, 
  open, 
  onOpenChange 
}) => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success("User ID copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return 'Unknown';
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>User Details</DialogTitle>
          <DialogDescription>
            Complete information about this user
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">User ID</div>
            <div className="flex items-center">
              <span className="text-sm mr-2 bg-gray-100 px-2 py-1 rounded font-mono">
                {user.id || user.user_id}
              </span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7" 
                onClick={() => copyToClipboard(user.id || user.user_id || '')}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium">First Name</div>
              <div className="text-sm">{user.first_name || 'Not provided'}</div>
            </div>
            <div>
              <div className="text-sm font-medium">Last Name</div>
              <div className="text-sm">{user.last_name || 'Not provided'}</div>
            </div>
          </div>
          
          <div>
            <div className="text-sm font-medium">Email</div>
            <div className="text-sm">{user.email || 'Not provided'}</div>
          </div>
          
          {user.company && (
            <div>
              <div className="text-sm font-medium">Company</div>
              <div className="text-sm">{user.company}</div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium">Country</div>
              <div className="text-sm">{user.country || 'Unknown'}</div>
            </div>
            <div>
              <div className="text-sm font-medium">Joined</div>
              <div className="text-sm">{formatDate(user.created_at)}</div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium">Primary Language</div>
              <div className="text-sm">{user.language || 'Not specified'}</div>
            </div>
            <div>
              <div className="text-sm font-medium">Secondary Language</div>
              <div className="text-sm">{user.language_secondary || 'None'}</div>
            </div>
          </div>
          
          {typeof user.journal_count === 'number' && (
            <div>
              <div className="text-sm font-medium">Journal Entries</div>
              <div className="text-sm">{user.journal_count}</div>
            </div>
          )}
          
          {user.is_placeholder && (
            <div className="bg-amber-50 p-3 rounded-md text-amber-700 text-sm mt-4">
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 mr-2" />
                <span className="font-medium">Limited Data Access</span>
              </div>
              <p className="mt-1">
                This is placeholder data. Full user details cannot be accessed.
              </p>
            </div>
          )}
          
          <div className="flex justify-end gap-2 mt-6">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            {user.email && (
              <Button
                onClick={() => {
                  const subject = `Regarding Clou user (UID: ${user.id || user.user_id})`;
                  window.location.href = `mailto:${user.email}?subject=${encodeURIComponent(subject)}`;
                }}
              >
                <Mail className="h-4 w-4 mr-2" />
                Email User
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailDialog;
